import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail } from "lucide-react";
import { CONTACT_INFO, IMAGES } from "../data";

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1200);
  const location = useLocation();

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1200);
      if (window.innerWidth >= 1200) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const navLinks = [
    { name: "Forside", path: "/" },
    { name: "Tømrerarbejde", path: "/toemrerarbejde" },
    { name: "Terrasse", path: "/terrasse" },
    { name: "Tagarbejde", path: "/tagarbejde" },
    { name: "Murer & Belægning", path: "/murer-belaegning" },
    { name: "Om os", path: "/om-os" },
    { name: "Kontakt", path: "/kontakt" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 rounded-none ${
        isScrolled || isOpen
          ? "bg-zinc-950/95 border-b border-zinc-800/80 backdrop-blur-md py-4"
          : "bg-zinc-950/80 border-b border-zinc-900/50 backdrop-blur-sm py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center group">
          <img 
            src="https://assets.auxodev.dk/cdn-cgi/image/width=240,quality=90,format=auto/assets/bf156273-6242-48dd-a9a0-7699f46947d9/logo-nords-hvid.png" 
            alt="Nordsjællands Tømrer og Snedker" 
            className="h-10 w-auto object-contain transition-transform duration-300 group-hover:scale-[1.02]"
          />
        </Link>

        {/* Desktop Navigation (>= 1200px) */}
        {isDesktop && (
          <nav className="flex items-center gap-8">
            <ul className="flex items-center gap-8">
              {navLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className={`text-xs font-semibold uppercase tracking-widest transition-colors duration-200 hover:text-amber-500 ${
                      isActive(link.path)
                        ? "text-amber-500 border-b-2 border-amber-500 pb-1"
                        : "text-stone-300"
                    }`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>

            {/* Divider */}
            <div className="h-8 w-[1px] bg-zinc-800" />

            {/* Quick Contact */}
            <div className="flex flex-col justify-center items-end">
              <a
                href={`tel:${CONTACT_INFO.phone}`}
                className="flex items-center gap-2 text-xs font-bold text-stone-100 uppercase tracking-wider hover:text-amber-500 transition-colors duration-200 whitespace-nowrap"
              >
                <Phone className="w-3 h-3 text-amber-500" />
                <span>{CONTACT_INFO.phoneFormatted}</span>
              </a>
              <a
                href={`mailto:${CONTACT_INFO.email}`}
                className="flex items-center gap-2 text-[10px] font-medium text-stone-400 hover:text-amber-500 transition-colors duration-200 whitespace-nowrap mt-0.5"
              >
                <Mail className="w-3 h-3 text-amber-500" />
                <span>{CONTACT_INFO.email}</span>
              </a>
            </div>
          </nav>
        )}

        {/* Mobile Menu Button (< 1200px) */}
        {!isDesktop && (
          <div className="flex items-center gap-4">
            {/* Quick Phone Icon */}
            <a
              href={`tel:${CONTACT_INFO.phone}`}
              className="p-2 text-stone-300 hover:text-amber-500 transition-colors duration-200 whitespace-nowrap"
              title="Ring op"
            >
              <Phone className="w-5 h-5" />
            </a>

            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 text-stone-300 hover:text-amber-500 transition-colors duration-200 focus:outline-none"
              aria-label="Åbn/luk menu"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        )}
      </div>

      {/* Mobile Navigation Drawer (< 1200px) */}
      {!isDesktop && isOpen && (
        <div className="absolute top-full left-0 right-0 bg-zinc-950 border-b border-zinc-800 flex flex-col px-6 py-8 space-y-6 animate-fadeIn">
          <ul className="flex flex-col space-y-4">
            {navLinks.map((link) => (
              <li key={link.path}>
                <Link
                  to={link.path}
                  className={`block text-sm font-semibold uppercase tracking-widest py-2 border-b border-zinc-900 hover:text-amber-500 transition-colors duration-200 ${
                    isActive(link.path) ? "text-amber-500" : "text-stone-300"
                  }`}
                >
                  {link.name}
                </Link>
              </li>
            ))}
          </ul>

          <div className="pt-4 flex flex-col space-y-3">
            <span className="text-xs font-semibold uppercase tracking-widest text-zinc-500">
              Direkte kontakt
            </span>
            <a
              href={`tel:${CONTACT_INFO.phone}`}
              className="flex items-center gap-3 text-sm font-bold text-stone-100 hover:text-amber-500 transition-colors duration-200 whitespace-nowrap"
            >
              <Phone className="w-4 h-4 text-amber-500" />
              <span>{CONTACT_INFO.phoneFormatted}</span>
            </a>
            <a
              href={`mailto:${CONTACT_INFO.email}`}
              className="flex items-center gap-3 text-sm font-medium text-stone-300 hover:text-amber-500 transition-colors duration-200 whitespace-nowrap"
            >
              <Mail className="w-4 h-4 text-amber-500" />
              <span>{CONTACT_INFO.email}</span>
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
